<script setup lang="ts">
import DateSpecificHours from '@/assets/date-specific-hours.png'
import {
  groupConsecutiveDates,
  UIMultiDatePicker,
} from '@gohighlevel/ghl-calendar-element'
import {
  InfoCircleIcon,
  PlusIcon,
  Trash02Icon,
  XCloseIcon,
} from '@gohighlevel/ghl-icons/24/outline'
import {
  UIButton,
  UIContentWrap,
  UIModal,
  UIModalContent,
  UIModalFooter,
  UITimePicker,
  UITooltip,
} from '@gohighlevel/ghl-ui'
import moment from 'moment'
import { computed, nextTick, PropType, ref, watch, watchEffect } from 'vue'
import DependentSettings from '../common/DependentSettings.vue'

import AppState from '../../states/app'

import { Schedule } from '@/components/Schedule/Schedule'
import { CalendarType } from '@/models/calendar'
import calendarScheduleState from '@/states/calendarScheduleState'
import {
  DatePickerLanguageMapper,
  GHLLanguageKeysMapper,
} from '@/utils/constants'
import { MyAvailability } from '../../class/pages/MyAvailability'
import { getLocationTzTimezone } from '../../utils/timezones'

moment.locale(GHLLanguageKeysMapper[AppState.locale])

const myAvailability = ref(new MyAvailability(''))
const showOverRideModal = ref(false)
const activeId = ref('')
const overriddenDates = ref<string[]>([])
const originalDatesInEdit = ref<string[]>([]) // Track original dates when editing a group
const showDateSelectionError = ref(false)

const props = defineProps({
  isRecurringEnabled: Boolean,
  toggleRecurring: Function,
  schedule: {
    type: Object as PropType<Schedule>,
    required: false,
    default: () => null,
  },
})

const editMode = ref(false)
const overriddenHours = ref([
  {
    startTime: '8:0',
    endTime: '17:0',
  },
])
const customSlotSelectorModal = ref()
const showDependentSettings = ref(false)
const editingCalendar = computed(() => {
  return AppState.editingCalendar
})

const locale = computed(() => {
  return DatePickerLanguageMapper[AppState.locale]
})

// Group consecutive dates with the same hours using package helper
// Transform MyAvailabilityDay structure to match package helper format
const groupedAvailableDays = computed(() => {
  const days = myAvailability.value.availableDays
  if (days.length === 0) return []

  const transformed = days.map(day => {
    const parsed = moment(day.day, 'MMMM D, YYYY')
    const dateStr = `${parsed.year()}-${String(parsed.month() + 1).padStart(
      2,
      '0'
    )}-${String(parsed.date()).padStart(2, '0')}`
    return {
      date: dateStr,
      intervals: (day.hours || [])
        .filter(h => h.startTime && h.endTime)
        .map(h => ({
          from: h.startTime!,
          to: h.endTime!,
        })),
      originalDay: day,
    }
  })

  // Use package helper to group (matching DateSpecificScheduleUI.vue pattern)
  const grouped = (groupConsecutiveDates as any)(transformed, locale.value)

  // Transform back to our structure
  return grouped.map(group => ({
    days: group.dates.map((d: any) => d.originalDay),
    displayText: group.displayText,
    hours: group.intervals.map((interval: any) => ({
      startTime: interval.from,
      endTime: interval.to,
    })),
  }))
})

const onClickAddSpecificHours = () => {
  if (props.isRecurringEnabled) {
    showDependentSettings.value = true
  } else {
    submitCallback()
  }
}

const submitCallback = () => {
  if (overlappingTimes.value.length > 0) {
    return
  }

  if (overriddenDates.value.length === 0) {
    showDateSelectionError.value = true
    return
  }

  // Clear the error message when dates are selected
  showDateSelectionError.value = false

  // If in edit mode, remove dates that were in the original group but are no longer selected
  if (editMode.value && originalDatesInEdit.value.length > 0) {
    const formatDate = (dateString: string) =>
      moment(new Date(dateString)).format('MMMM D, YYYY')

    const currentDatesSet = new Set(overriddenDates.value.map(formatDate))

    const originalFormattedDates = originalDatesInEdit.value.map(formatDate)

    originalFormattedDates.forEach(originalDate => {
      if (!currentDatesSet.has(originalDate)) {
        myAvailability.value.removeAvailabilityDay(originalDate)
      }
    })
  }

  overriddenDates.value.forEach(dateString => {
    const date = new Date(dateString)
    const formattedDate = moment(date).format('MMMM D, YYYY')

    let target = myAvailability.value.availableDays.find(item => {
      return item.day === formattedDate
    })

    if (target) {
      target.hours =
        overriddenHours.value?.length === 0
          ? []
          : [
              ...new Set([
                ...overriddenHours.value.map(x => ({
                  startTime: x.startTime,
                  endTime: x.endTime,
                })),
              ]),
            ]
    } else {
      myAvailability.value.addAvailabilityDay(formattedDate)
      target = myAvailability.value.availableDays.find(
        item => item.day === formattedDate
      )
      if (target) {
        target.hours =
          overriddenHours.value?.length === 0
            ? []
            : overriddenHours.value.map(x => ({
                startTime: x.startTime,
                endTime: x.endTime,
              }))
      }
    }
  })

  showOverRideModal.value = false
  activeId.value = ''
  editMode.value = false
  originalDatesInEdit.value = []
}

const cancelCallback = () => {
  showOverRideModal.value = false
  overriddenDates.value = []
  originalDatesInEdit.value = []
  overriddenHours.value = [
    {
      startTime: '8:0',
      endTime: '17:0',
    },
  ]
  activeId.value = ''
  editMode.value = false
  showDateSelectionError.value = false
}

const methods = {
  async loadData() {
    if (
      AppState.isScheduleEnabled &&
      editingCalendar.value?.calendarType === CalendarType.EVENT
    ) {
      //load custom availability from schedule
      if (props.schedule) {
        const availability = props.schedule?.getCustomAvailability(
          editingCalendar.value?.id || ''
        )
        myAvailability.value.setCustomAvailabilityDataFromDatabase(availability)
      } else {
        const schedule = await calendarScheduleState.getEventCalendarSchedules(
          editingCalendar.value?.id
        )
        const availability = schedule.getCustomAvailability(
          editingCalendar.value?.id || ''
        )
        myAvailability.value.setCustomAvailabilityDataFromDatabase(availability)
      }
    } else {
      myAvailability.value.setCustomAvailabilityDataFromDatabase(
        editingCalendar.value?.availabilities || []
      )
    }
  },
  async addNextSlots(overriddenHours, item) {
    overriddenHours.push({
      startTime: moment(item.endTime, 'H:m')
        .set({
          hour: moment(item.endTime, 'H:m').hour() + 1,
        })
        .format('H:m'),
      endTime: moment(item.endTime, 'H:m')
        .set({
          hour: moment(item.endTime, 'H:m').hour() + 2,
        })
        .format('H:m'),
    })
    await nextTick()
    customSlotSelectorModal.value.scrollTop =
      customSlotSelectorModal.value.scrollHeight
  },
  async removeHours(index) {
    overriddenHours.value.length > 0
      ? overriddenHours.value.splice(index, 1)
      : (overriddenHours.value = [])
  },

  dateDisabled(date: Date) {
    const selectedDate = moment(date).startOf('day')
    const currentDate = getLocationTzTimezone(
      AppState.locationTimezone
    ).startOf('day')

    return selectedDate.isBefore(currentDate)
  },
  getAvailability() {
    const openHours = myAvailability.value.availableDays.map(day =>
      day.serializeCustomAvailabilityAsPerAPIFormat()
    )
    const existingOpenHourIds = editingCalendar.value?.availabilities
      ?.map(x => x._id)
      ?.filter(x => x)

    const newOpenHourIds = openHours?.map(x => x!.id)?.filter(x => x)

    const idsToDelete = existingOpenHourIds?.filter(
      x => !newOpenHourIds.includes(x)
    )

    idsToDelete?.forEach(id => {
      const openHour = editingCalendar.value?.availabilities.find(
        x => x._id === id
      )

      openHours.push({
        date: openHour?.date,
        hours: openHour?.hours,
        id: openHour?._id,
        deleted: true,
      })
    })

    return openHours
  },
  addCustomAvailabilityDay() {
    let day = ''

    const days = myAvailability.value.availableDays.map(x =>
      moment(x.day, 'MMMM D, YYYY').tz(AppState.locationTimezone)
    )

    if (
      days.length > 0 &&
      days[days.length - 1].isSameOrAfter(
        moment().tz(AppState.locationTimezone),
        'day'
      )
    ) {
      day = days[days.length - 1].add(1, 'days').format('MMMM D, YYYY')
    } else {
      day = moment()
        .tz(AppState.locationTimezone)

        .format('MMMM D, YYYY')
    }

    myAvailability.value.addAvailabilityDay(day)
  },
  hasErrors() {
    // return myAvailability.value.hasAnyInvalidHours
    return false
  },
  getAvailabilityLength() {
    return myAvailability.value.availableDays.length
  },
  resetCustomAvailability() {
    myAvailability.value.availableDays = []
  },
  openOverrideModal() {
    showOverRideModal.value = true
    editMode.value = false
    overriddenHours.value = [
      {
        startTime: '8:0',
        endTime: '17:0',
      },
    ]
    overriddenDates.value = []
    showDateSelectionError.value = false
  },
}

const invalidTimes = computed(() => {
  return (
    overriddenHours.value.filter(x => {
      if (
        (x.startTime === '0:0' && x.endTime === '0:0') ||
        x.endTime === '0:0'
      ) {
        return false
      }
      return (
        moment(x.startTime, 'H:m').isSameOrAfter(moment(x.endTime, 'H:m')) ||
        moment(x.endTime, 'H:m').isSameOrBefore(moment(x.startTime, 'H:m'))
      )
    }).length > 0
  )
})

const has24hourError = computed(() => {
  overriddenHours.value.forEach(h => {
    if (
      h.startTime === '0:0' &&
      h.endTime === '0:0' &&
      overriddenHours.value.length > 1
    ) {
      return true
    }
  })
  return false
})

const is24HourSelected = computed(() => {
  return overriddenHours.value.length === 1
    ? overriddenHours.value[0].startTime === '0:0' &&
        overriddenHours.value[0].endTime === '0:0'
    : false
})

const overlappingTimes = computed(() => {
  const overlaps = [] as number[]

  if (
    overriddenHours.value.length === 1 &&
    overriddenHours.value[0].startTime === '0:0' &&
    overriddenHours.value[0].endTime === '0:0'
  ) {
    return []
  }

  // For multi-date picker, skip overlap check if no dates selected
  if (overriddenDates.value.length === 0) {
    return []
  }

  for (let i = 0; i < overriddenHours.value.length - 1; i++) {
    for (let j = i + 1; j < overriddenHours.value.length; j++) {
      const startTime1 = moment(overriddenHours.value[i].startTime, 'H:mm')
      const endTime1 = moment(overriddenHours.value[i].endTime, 'H:mm')
      const startTime2 = moment(overriddenHours.value[j].startTime, 'H:mm')
      const endTime2 = moment(overriddenHours.value[j].endTime, 'H:mm')

      if (
        startTime1.isBetween(startTime2, endTime2) ||
        endTime1.isBetween(startTime2, endTime2) ||
        startTime2.isBetween(startTime1, endTime1) ||
        endTime2.isBetween(startTime1, endTime1)
      ) {
        overlaps.push(i)
        overlaps.push(j)
      }
    }
  }

  return [...new Set(overlaps)]
})

watch(
  () => [editingCalendar.value, props.schedule],
  () => {
    methods.loadData()
  },
  { immediate: true }
)

watch(
  () => overriddenDates.value,
  () => {
    if (overriddenDates.value.length > 0) {
      showDateSelectionError.value = false
    }
  }
)

watchEffect(() => {
  // For multi-date picker, check if all selected dates have the same hours
  // If editing a single date, use that date's hours
  if (overriddenDates.value.length > 0 && editMode.value) {
    // In edit mode, use the first selected date's hours
    const firstDate = new Date(overriddenDates.value[0])
    const formattedDate = moment(firstDate).format('MMMM D, YYYY')
    const existsHour = myAvailability.value.availableDays.find(
      x => x.day === formattedDate
    ) as any

    if (existsHour) {
      overriddenHours.value = existsHour.hours.map(x => ({
        startTime: x.startTime,
        endTime: x.endTime,
      }))
    } else {
      overriddenHours.value = [
        {
          startTime: '8:0',
          endTime: '17:0',
        },
      ]
    }
  } else if (overriddenDates.value.length === 0) {
    // Reset to default when no dates selected
    overriddenHours.value = [
      {
        startTime: '8:0',
        endTime: '17:0',
      },
    ]
  }
})

const handleEditGroup = (group: { days: any[]; hours: any[] }) => {
  // When editing a group, edit all dates in the group
  const firstDay = group.days[0]
  activeId.value = firstDay.id || ''
  editMode.value = true
  overriddenDates.value = group.days.map(day => {
    const date = moment(day.day, 'MMMM D, YYYY').startOf('day')
    return date.toISOString()
  })
  originalDatesInEdit.value = [...overriddenDates.value]
  overriddenHours.value = group.hours.map(x => ({
    startTime: x.startTime,
    endTime: x.endTime,
  }))
  showOverRideModal.value = true
}

const handleDeleteGroup = (group: { days: any[] }) => {
  // Remove all dates in the group
  group.days.forEach(day => {
    myAvailability.value.removeAvailabilityDay(day.day)
  })
}

const cancelDependentSettings = () => {
  showDependentSettings.value = false
}

const confirmDependentSettings = () => {
  if (props.toggleRecurring) {
    props.toggleRecurring(false)
  }
  submitCallback()
  showDependentSettings.value = false
}

defineExpose({
  methods,
})
</script>
<template>
  <UIContentWrap :locale="GHLLanguageKeysMapper[AppState.locale]" :style="{ padding: '18px 32px 32px 32px' }">
    <div>
      <div class="mb-4 flex items-center space-x-3">
        <p class="text-sm font-medium text-gray-700">
          {{ $t('calendar_advanced.availability.specific_hours_button') }}
        </p>

        <UITooltip :placement="'top'">
          <template #trigger>
            <InfoCircleIcon class="w-4 text-gray-500" />
          </template>
          <p class="max-w-sm">
            {{
              $t('calendar_advanced.availability.date_specific_hours_tooltip')
            }}
          </p>
        </UITooltip>
      </div>
      <p class="mb-4 text-sm text-gray-500">
        {{ $t('calendar_advanced.availability.date_specific_hours_disabled_conditions_tooltip') }}
      </p>
      <UIButton
        id="add-override"
        v-model="showOverRideModal"
        type="default"
        size="small"
        class="w-full overflow-hidden px-2 text-xs"
        :title="$t('calendar_advanced.availability.date_specific_level')"
        @click="methods.openOverrideModal"
      >
        <div class="text-[13px]">{{ $t('calendar_advanced.availability.date_specific_level') }}</div>
      </UIButton>

      <div
        v-if="myAvailability.availableDays.length === 0"
        class="my-20 flex flex-col items-center justify-center"
      >
        <img
          :src="DateSpecificHours"
          class="mb-2 h-36"
          :alt="$t('alt_text.date_specific_hours')"
        />
        <p class="text-md text-center font-semibold text-gray-900">
          {{ $t('calendar_advanced.availability.no_date_specific_heading') }}
        </p>

        <p class="text-center text-xs text-gray-600">
          {{
            $t('calendar_advanced.availability.no_date_specific_description')
          }}
        </p>
      </div>

      <div v-else>
        <div
          v-for="(group, index) in groupedAvailableDays"
          :key="index"
          class="mt-4 flex cursor-pointer items-center justify-between rounded border-[1px] border-gray-200 px-2 py-4"
          :class="{
            'line-through': methods.dateDisabled(
              moment(group.days[0].day, 'MMMM D, YYYY').toDate()
            ),
          }"
          role="button"
          @click="handleEditGroup(group)"
          @keydown="handleEditGroup(group)"
          tabindex="0"
        >
          <div class="flex-1">
            <p class="text-sm font-medium text-gray-700">
              {{ group.displayText }}
            </p>
            <p v-if="group.hours.length === 0" class="text-sm text-gray-500">
              {{ $t('calendar_advanced.availability.unavailable_label') }}
            </p>
            <p
              v-for="(hour, hourIndex) in group.hours"
              v-else
              :key="hourIndex"
              class="mt-1 text-[13px] font-normal text-gray-500"
            >
              {{ moment(hour.startTime, 'HH:mm').format('hh:mm A') }} -
              {{ moment(hour.endTime, 'HH:mm').format('hh:mm A') }}
            </p>
          </div>
          <UIButton
            id="remove-availability-hour-custom"
            class="px-3 text-red-600"
            :text="true"
            :aria-label="$t('aria_labels.remove_custom_slot')"
            @click.stop="handleDeleteGroup(group)"
          >
            <Trash02Icon class="w-4" />
          </UIButton>
        </div>
      </div>
      <UIModal :show="showOverRideModal" :width="440">
        <template #header>
          <div class="flex w-full items-center justify-between">
            <p class="w-3/4 text-[16px] text-gray-900">
              {{ $t('calendar_advanced.availability.subTitle') }}
              <span class="hidden">{{ overriddenDates }}</span>
            </p>
            <XCloseIcon
              class="h-5 w-5 cursor-pointer text-gray-500"
              :aria-label="$t('aria_labels.close_modal')"
              role="button"
              @click="cancelCallback()"
              @keydown="cancelCallback()"
            />
          </div>
        </template>
        <UIModalContent>
          <div
            id="do-picker"
            class="mb-2 flex transform items-center justify-center rounded-lg border-[1px] border-gray-200 py-2"
          >
            <UIMultiDatePicker
              :custom-style="{
                width: '400px',
              }"
              id="date-override picker"
              v-model="overriddenDates"
              :panel-view="true"
              :language="locale"
              :hide-input="true"
              :disabled-dates="methods.dateDisabled"
              :min-date="
                moment().tz(AppState.locationTimezone).startOf('day').toDate()
              "
              class="scale-125 transform"
            />
          </div>
          <p
            v-if="showDateSelectionError"
            class="mb-2 mt-2 text-left text-sm text-red-600"
          >
            {{ $t('calendar_advanced.availability.please_select_date') }}
          </p>
          <div
            v-if="overriddenDates.length > 0"
            class="border-[1px] border-gray-200 p-4"
          >
            <div class="flex items-center justify-between">
              <p class="mb-3 text-sm font-semibold text-gray-800">
                {{ $t('calendar_advanced.availability.heading_2') }}
              </p>
              <UIButton
                v-if="overriddenHours.length === 0"
                id="add-override-custom"
                :aria-label="$t('aria_labels.add_custom_slot')"
                :text="true"
                class="text-gray-700"
                @click="
                  overriddenHours.push({
                    startTime: '8:0',
                    endTime: '17:0',
                  })
                "
              >
                <PlusIcon class="w-4" />
              </UIButton>
            </div>

            <div
              v-if="overriddenDates.length > 0"
              ref="customSlotSelectorModal"
              class="max-h-40 overflow-y-auto"
            >
              <div v-if="overriddenHours.length == 0">
                <p class="text-md font-semibold text-gray-600">
                  {{ $t('calendar_advanced.availability.unavailable_label') }}
                </p>
              </div>
              <div
                v-for="(item, index) in overriddenHours"
                :key="index"
                class="mb-2 flex items-center gap-2"
              >
                <UITimePicker
                  v-model:formatted-value="item.startTime"
                  value-format="H:m"
                  :minutes="5"
                  format="hh:mm a"
                  use-12-hours
                  :actions="['confirm']"
                  class="w-full"
                  :status="
                    !item.startTime ||
                    overlappingTimes.includes(index) ||
                    (moment(item.endTime, 'H:m').isSameOrBefore(
                      moment(item.startTime, 'H:m')
                    ) &&
                      item.endTime !== '0:0' &&
                      item.startTime !== '0:0')
                      ? 'error'
                      : 'success'
                  "
                />

                <div class="w-8 min-w-max font-semibold capitalize">
                  {{ $t('calendar_advanced.availability.to') }}
                </div>
                <UITimePicker
                  v-model:formatted-value="item.endTime"
                  value-format="H:m"
                  :minutes="5"
                  format="hh:mm a"
                  use-12-hours
                  :actions="['confirm']"
                  class="w-full"
                  :status="
                    !item.endTime ||
                    overlappingTimes.includes(index) ||
                    (moment(item.endTime, 'H:m').isSameOrBefore(
                      moment(item.startTime, 'H:m')
                    ) &&
                      item.endTime !== '0:0' &&
                      item.startTime !== '0:0')
                      ? 'error'
                      : 'success'
                  "
                />
                <div class="mr-6 flex items-center justify-around">
                  <div class="ml-4 w-4">
                    <UIButton
                      v-if="index === overriddenHours.length - 1"
                      id=""
                      :text="true"
                      class="text-gray-700"
                      :disabled="is24HourSelected"
                      :aria-label="$t('aria_labels.add_custom_slot')"
                      @click="methods.addNextSlots(overriddenHours, item)"
                    >
                      <PlusIcon class="w-4" />
                    </UIButton>
                  </div>
                  <div class="ml-4 w-4">
                    <UIButton
                      id="add-override-hour-custom"
                      class="text-red-600"
                      :text="true"
                      :aria-label="$t('aria_labels.remove_custom_slot')"
                      @click="methods.removeHours(index)"
                    >
                      <Trash02Icon class="w-4" />
                    </UIButton>
                  </div>
                </div>
              </div>
            </div>
            <p
              v-if="overlappingTimes.length > 0"
              class="text-left text-sm text-red-600"
            >
              {{ $t('calendar_advanced.availability.overlap_error') }}
            </p>
          </div>
        </UIModalContent>
        <template #footer>
          <UIModalFooter
            id="modal-footer"
            :positive-text="$t('labels.submit')"
            :negative-text="$t('labels.cancel')"
            :disabled="
              overlappingTimes.length > 0 ||
              methods.hasErrors() ||
              invalidTimes ||
              has24hourError
            "
            @positive-click="onClickAddSpecificHours"
            @negative-click="cancelCallback"
          />
        </template>
      </UIModal>

      <DependentSettings
        v-if="showDependentSettings"
        :show-modal="showDependentSettings"
        :title="$t('calendar_advanced.availability.enable_date_specific_hours')"
        :desc="
          $t('calendar_advanced.availability.enable_date_specific_hours_desc')
        "
        :settings="[
          $t('calendar_advanced.availability.recurring_meetings_disabled'),
          {
            contactsLabel:
              AppState.customObjectLabels?.contact?.plural?.toLowerCase(),
          },
        ]"
        @confirm-click="confirmDependentSettings"
        @cancel-click="cancelDependentSettings"
      />
    </div>
  </UIContentWrap>
</template>
<style lang="scss">
#do-picker {
  --primary-600: #344054 !important;

  .n-date-panel-weekdays {
    font-weight: 500 !important;
  }

  .n-date-panel-month__prev {
    margin-left: -50px;
    transform: scale(1.5);
  }

  .n-date-panel-month__next {
    margin-right: -50px;
    transform: scale(1.5);
  }

  .n-date-panel-month__fast-next {
    height: 0px !important;
    width: 0px !important;
  }

  .n-date-panel-month__fast-prev {
    height: 0px !important;
    width: 0px !important;
  }

  .n-tabs {
    height: 90%;
    overflow: hidden;
  }

  .n-tab-pane {
    height: 90% !important;
    overflow: scroll !important;
  }
}
</style>
